import {
  CobaltDropdown,
  CobaltDropdownItem,
} from '@cae/cobalt-react/components/dropdown';
import { CobaltFileTile } from '@cae/cobalt-react/components/file-tile';
import { CobaltFileUploader } from '@cae/cobalt-react/components/file-uploader';
import {
  CobaltFormField,
  CobaltFormFieldHelperText,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { CobaltToggletip } from '@cae/cobalt-react/components/toggletip';
import { CobaltIconInfoCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconInfoCircleSolid';
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_NOTES_LENGTH,
} from '@features/documents/utils/documentConstants';
import * as React from 'react';

import type { SubmitDocumentFormProps } from './SubmitDocumentForm.types';

import {
  AUTHORITY_OPTIONS,
  getNotesCharCount,
} from '../../SubmitDocumentPanel.utils';
import styles from './SubmitDocumentForm.module.css';

const SubmitDocumentForm = React.memo(
  ({
    formState,
    uploaderFiles,
    showValidation,
    isSubmitting,
    isSuccess,
    uploadResponse,
    onAuthorityChange,
    onFilesChange,
    onNotesChange,
  }: Readonly<SubmitDocumentFormProps>): React.ReactElement => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleToggletipClose = React.useCallback((): void => {
      setAnchorEl(null);
    }, []);

    const handleToggletipClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (anchorEl) {
          handleToggletipClose();
        } else {
          setAnchorEl(event.currentTarget);
        }
      },
      [anchorEl, handleToggletipClose]
    );

    const handleFileRemove = React.useCallback(
      (): void => {
        onFilesChange([]);
      },
      [onFilesChange]
    );

    const handleFileClick = React.useCallback((): void => undefined, []);

    const formDisabled = isSubmitting || isSuccess;
    const effectiveFileItem = React.useMemo(() => {
      const validFile = uploaderFiles.find(f => !f.invalid);
      if (validFile === undefined) return undefined;
      if (isSuccess) return { ...validFile, state: 'complete' as const, uploaded: true };
      if (isSubmitting) return { ...validFile, state: 'loading' as const };
      return validFile;
    }, [uploaderFiles, isSuccess, isSubmitting]);

    return (
      <div className={styles.formFields} data-testid="submit-document-form">
        {!isSuccess && (
          <CobaltFormField
            id="authority"
            error={
              showValidation && !formState.authority
                ? 'This field is required'
                : undefined
            }
          >
            <CobaltFormFieldLabel
              htmlFor="authority"
              label="What authority is this document associated with?"
            />
            <CobaltDropdown
              id="authority"
              value={formState.authority}
              onSelectionChange={onAuthorityChange}
              disabled={formDisabled}
              placeholder="Select an authority"
            >
              {AUTHORITY_OPTIONS.map(option => (
                <CobaltDropdownItem key={option.value} value={option.value}>
                  {option.label}
                </CobaltDropdownItem>
              ))}
            </CobaltDropdown>
            {showValidation && !formState.authority && (
              <CobaltFormFieldHelperText
                helperId="authority-helper"
                error="This field is required"
              />
            )}
          </CobaltFormField>
        )}

        {effectiveFileItem !== undefined ? (
          <div role="list" className={styles.fileTileList}>
            <CobaltFileTile
              fileItem={effectiveFileItem}
              variant={formDisabled ? 'read-only' : 'default'}
              hasRemoveOnUploadedItem={!formDisabled}
              isFileClickable={false}
              onRemove={handleFileRemove}
              onFileClick={handleFileClick}
            />
          </div>
        ) : (
          <CobaltFileUploader
            variant="drop"
            label="Upload file"
            acceptMultipleFiles={false}
            fileExtensions={ACCEPTED_FILE_TYPES}
            maxFileSize={MAX_FILE_SIZE_BYTES / 1024}
            hasRemoveOnUploadedItem
            files={uploaderFiles}
            onFilesChange={onFilesChange}
            isLoading={isSubmitting}
            isSuccess={isSuccess}
            uploadResponse={uploadResponse}
            disabled={formDisabled}
            hasError={showValidation && !formState.file}
            description={
              <div className={styles.fileUploaderDescription}>
                <p className="body-02">
                  Total upload size shouldn't exceed 100MB.
                </p>
                <span className={styles.fileTypeInfo}>
                  <button
                    className={styles.supportedFilesButton}
                    onClick={handleToggletipClick}
                  >
                    <CobaltIconInfoCircleSolid
                      className={styles.supportedFilesIcon}
                    />
                  </button>
                  <CobaltToggletip
                    title="The following file types are accepted."
                    anchorEl={anchorEl}
                    onToggle={handleToggletipClose}
                    isOpen={Boolean(anchorEl)}
                  >
                    <div className={styles.toggletipContent}>
                      {ACCEPTED_FILE_TYPES.join('\n')
                        .split('\n')
                        .map(type => (
                          <span key={type}>{type}</span>
                        ))}
                    </div>
                  </CobaltToggletip>
                  Supported file types
                </span>
              </div>
            }
          />
        )}

        <CobaltFormField
          id="notes"
          error={
            showValidation && !formState.notes.trim()
              ? 'This field is required'
              : undefined
          }
        >
          <CobaltFormFieldLabel
            htmlFor="notes"
            label="Why are you submitting this document?"
            labelAccessory={getNotesCharCount(formState.notes)}
            className={styles.notesLabel}
          />
          <CobaltTextInput
            id="notes"
            value={formState.notes}
            onChange={onNotesChange}
            disabled={formDisabled}
            multiline
            placeholder="Add notes"
            maxLength={MAX_NOTES_LENGTH}
            rows={8}
          />
          {showValidation && !formState.notes.trim() && (
            <CobaltFormFieldHelperText
              helperId="notes-helper"
              error="This field is required"
            />
          )}
        </CobaltFormField>
      </div>
    );
  }
);

SubmitDocumentForm.displayName = 'SubmitDocumentForm';

export { SubmitDocumentForm };
